/** Avaliação de Casal — Coach Virtual.
 * Convite por email registado, aceitação, radar sobreposto dos dois e
 * análise IA: valores comuns/compatíveis/incompatíveis, riscos de
 * conflito nos metaprogramas e graus de compatibilidade.
 */
import { useEffect, useMemo, useState } from "react";
import {
  HeartHandshake, Loader2, Mail, RefreshCw, XCircle, CheckCircle2,
  AlertTriangle, Sparkles, Users, FileDown, Target, Trash2, BookmarkPlus,
  Share2, Copy, Download, X,
} from "lucide-react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import { myApi } from "../../api";
import { verifyEmail } from "@/shared/emailValidation";

const RISCO_CLS = {
  baixo: "bg-emerald-100 text-emerald-700",
  "médio": "bg-amber-100 text-amber-700",
  medio: "bg-amber-100 text-amber-700",
  alto: "bg-rose-100 text-rose-700",
};

export default function RodaCasal({ onError }) {
  const [estado, setEstado] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    try { setEstado(await myApi.casalEstado()); }
    catch (e) { onError?.(e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const run = async (fn, okMsg) => {
    setBusy(true); setMsg("");
    try {
      const r = await fn();
      setMsg(r?.message || okMsg || "");
      await load();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  if (!estado) return <p className="text-[#978D78] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> A carregar...</p>;

  return (
    <div className="space-y-6" data-testid="coach-casal">
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full grid place-items-center bg-[#4A5E2E] text-white"><HeartHandshake size={20} /></div>
          <div>
            <h2 className="text-lg font-semibold text-[#0F1829]">Avaliação de Casal</h2>
            <p className="text-xs text-[#6B7280]">Compara as vossas Rodas da Vida e descobre a compatibilidade do casal.</p>
          </div>
        </div>
        {msg && <p className="mt-3 text-sm rounded-lg bg-[#F5F2EB] px-3 py-2 text-[#0F1829]" data-testid="casal-msg">{msg}</p>}

        {estado.estado === "sem_ligacao" && (
          <div className="mt-4">
            <p className="text-sm text-[#4B5563] mb-3">
              Indica o email da outra pessoa (tem de estar <strong>registada no site</strong>).
              Cada um preenche a sua Roda da Vida e a análise de casal é actualizada com as avaliações mais recentes de ambos.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 max-w-lg">
              <div className="relative flex-1">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#978D78]" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                  placeholder="email@exemplo.com"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#E5E7EB] text-sm"
                  data-testid="casal-email-input" />
              </div>
              <button onClick={() => run(async () => {
                  const ev = await verifyEmail(email);
                  if (!ev.valid) {
                    throw new Error(ev.suggestion ? `${ev.message} Quis dizer ${ev.suggestion}?` : ev.message);
                  }
                  return myApi.casalConvite(email);
                })} disabled={busy || !email}
                className="px-5 py-2.5 rounded-lg bg-[#4A5E2E] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                data-testid="casal-convite-btn">
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Convidar"}
              </button>
            </div>
          </div>
        )}

        {estado.estado === "pendente_enviado" && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-[#4B5563] flex-1" data-testid="casal-pendente-enviado">
              Convite enviado a <strong>{estado.parceiro?.nome || estado.parceiro?.email}</strong>. Aguarda que aceite no Coach Virtual.
            </p>
            <button onClick={() => run(() => myApi.casalTerminar())} disabled={busy}
              className="text-sm text-rose-600 hover:underline" data-testid="casal-cancelar-btn">Cancelar convite</button>
          </div>
        )}

        {estado.estado === "pendente_recebido" && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="casal-pendente-recebido">
            <p className="text-sm text-[#4B5563] flex-1">
              <strong>{estado.parceiro?.nome}</strong> convidou-te para uma avaliação de casal.
            </p>
            <div className="flex gap-2">
              <button onClick={() => run(() => myApi.casalResponder(true))} disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A5E2E] text-white text-sm font-semibold"
                data-testid="casal-aceitar-btn"><CheckCircle2 size={15} /> Aceitar</button>
              <button onClick={() => run(() => myApi.casalResponder(false))} disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#E5E7EB] text-sm"
                data-testid="casal-recusar-btn"><XCircle size={15} /> Recusar</button>
            </div>
          </div>
        )}

        {estado.estado === "ativo" && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-[#4B5563] flex-1" data-testid="casal-ativo">
              Casal: <strong>tu</strong> + <strong>{estado.parceiro?.nome}</strong>
              {!estado.minha_avaliacao && " — falta a tua Roda da Vida."}
              {!estado.avaliacao_parceiro && ` — falta a Roda da Vida de ${(estado.parceiro?.nome || "").split(" ")[0]}.`}
            </p>
            <button onClick={() => { if (window.confirm("Terminar a avaliação de casal?")) run(() => myApi.casalTerminar()); }}
              className="text-xs text-rose-600 hover:underline" data-testid="casal-terminar-btn">Terminar</button>
          </div>
        )}
      </div>

      {estado.estado === "ativo" && (
        <CasalConteudo estado={estado} run={run} busy={busy}
          reload={(st) => (st ? setEstado(st) : load())} />
      )}
    </div>
  );
}

function CasalConteudo({ estado, run, busy, reload }) {
  const [gerando, setGerando] = useState(!!estado.analise_a_gerar);
  const [erroGerar, setErroGerar] = useState("");

  useEffect(() => { setGerando(!!estado.analise_a_gerar); }, [estado.analise_a_gerar]);

  useEffect(() => {
    if (!gerando) return undefined;
    const dataAntes = estado.analise_data;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const st = await myApi.casalEstado();
        if ((st.analise_data && st.analise_data !== dataAntes) || (!st.analise_a_gerar && tries > 2)) {
          clearInterval(t);
          setGerando(false);
          reload(st);
          if (!st.analise_data || st.analise_data === dataAntes) {
            setErroGerar("Não foi possível gerar a análise. Tenta novamente.");
          }
        }
      } catch { /* continua a tentar */ }
      // A geração via Claude Sonnet 4.6 costuma demorar 90-140s, mas pode
      // ir aos 4-5 min em picos. 60 tries × 6s = 6 min de folga total.
      if (tries >= 60) { clearInterval(t); setGerando(false); }
    }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [gerando]);

  const gerar = async () => {
    setErroGerar("");
    try {
      await myApi.casalAnalise();
      setGerando(true);
    } catch (e) { setErroGerar(e.message); }
  };

  const [metas, setMetas] = useState([]);
  const loadMetas = async () => {
    try { setMetas((await myApi.casalMetas()).metas || []); } catch { /* noop */ }
  };
  useEffect(() => { loadMetas(); }, []);

  const guardarMeta = async (rec) => {
    try {
      await myApi.casalMetaCriar({ titulo: rec.titulo, acao: rec.acao });
      await loadMetas();
    } catch (e) { alert(e.message); }
  };

  const [shareOpen, setShareOpen] = useState(false);

  const p2 = (estado.parceiro?.nome || "Parceiro(a)").split(" ")[0];
  const radarData = useMemo(() => {
    const s1 = estado.minha_avaliacao?.scores || {};
    const s2 = estado.avaliacao_parceiro?.scores || {};
    const areas = [...new Set([...Object.keys(s1), ...Object.keys(s2)])];
    return areas.map((a) => ({
      label: a === "Desenvolvimento Pessoal" ? "Desenv. Pessoal" : a,
      v1: Number(s1[a]) || 0, v2: Number(s2[a]) || 0,
    }));
  }, [estado]);

  const a = estado.analise;
  const podeGerar = estado.minha_avaliacao && estado.avaliacao_parceiro;

  return (
    <>
      {radarData.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm" data-testid="casal-radar-card">
          <h3 className="text-base font-semibold text-[#0F1829] mb-2 flex items-center gap-2"><Users size={16} /> As vossas Rodas da Vida</h3>
          <div className="w-full h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="75%">
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="label" tick={{ fill: "#0F1829", fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: "#978D78", fontSize: 10 }} />
                <Radar name="Tu" dataKey="v1" stroke="#4A5E2E" fill="#4A5E2E" fillOpacity={0.3} />
                <Radar name={p2} dataKey="v2" stroke="#B8860B" fill="#B8860B" fillOpacity={0.25} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <h3 className="text-base font-semibold text-[#0F1829] flex items-center gap-2"><Sparkles size={16} /> Análise de Compatibilidade</h3>
            {estado.analise_desatualizada && a && (
              <p className="text-xs text-amber-600 mt-1" data-testid="casal-desatualizada">
                Há avaliações mais recentes — actualiza a análise para reflectir os novos dados.
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {a && (
              <button onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A5E2E] text-white text-sm font-semibold hover:opacity-90"
                data-testid="casal-partilhar-btn">
                <Share2 size={15} /> Partilhar
              </button>
            )}
            {a && (
              <a href="/api/my/coach/casal/analise/pdf" target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#E5E7EB] text-sm font-semibold text-[#0F1829] hover:bg-[#F3F4F6]"
                data-testid="casal-pdf-btn">
                <FileDown size={15} /> PDF
              </a>
            )}
            <button onClick={gerar} disabled={gerando || !podeGerar}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0F1829] text-white text-sm font-semibold disabled:opacity-50"
              data-testid="casal-gerar-analise-btn">
              {gerando ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              {gerando ? "A gerar… (1-2 min)" : a ? "Actualizar análise" : "Gerar análise de casal"}
            </button>
          </div>
        </div>
        {erroGerar && <p className="text-sm text-rose-600 mt-2" data-testid="casal-erro-gerar">{erroGerar}</p>}
        {gerando && (
          <p className="text-sm text-[#978D78] mt-3" data-testid="casal-gerando">
            A analisar as vossas Rodas da Vida com o coach de PNL… a página actualiza automaticamente.
          </p>
        )}
        {!podeGerar && (
          <p className="text-sm text-[#978D78] mt-3">Ambos precisam de ter a Roda da Vida preenchida para gerar a análise.</p>
        )}
        {a && <AnaliseCasal a={a} p2={p2} metas={metas} onGuardarMeta={guardarMeta} />}
      </div>

      <MetasCard metas={metas} reload={loadMetas} />

      {shareOpen && <ShareCasalModal onClose={() => setShareOpen(false)} p1={(estado.parceiro?.nome || "").split(" ")[0]} />}
    </>
  );
}

function AnaliseCasal({ a, p2, metas = [], onGuardarMeta }) {
  const jaGuardada = (titulo) => metas.some((m) => m.titulo === titulo);
  const grau = Math.round(Number(a.compatibilidadeGlobal) || 0);
  const cor = grau >= 70 ? "#4A5E2E" : grau >= 45 ? "#C2831A" : "#B91C1C";
  return (
    <div className="mt-5 space-y-6" data-testid="casal-analise">
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-full grid place-items-center text-white text-xl font-bold shrink-0" style={{ background: cor }}
          data-testid="casal-compat-global">{grau}%</div>
        <div>
          <p className="text-sm font-semibold text-[#0F1829]">Compatibilidade global</p>
          <p className="text-sm text-[#4B5563] mt-1">{a.resumo}</p>
        </div>
      </div>

      {(a.grausPorArea || []).length > 0 && (
        <Sec title="Grau de compatibilidade por área">
          <div className="space-y-2.5">
            {a.grausPorArea.map((g) => (
              <div key={g.area}>
                <div className="flex justify-between text-sm"><span className="font-medium text-[#0F1829]">{g.area}</span><span className="text-[#978D78]">{Math.round(g.grau)}%</span></div>
                <div className="h-1.5 bg-[#F3F0E7] rounded-full mt-1 mb-1">
                  <div className="h-1.5 rounded-full" style={{ width: `${Math.max(4, g.grau)}%`, background: g.grau >= 70 ? "#4A5E2E" : g.grau >= 45 ? "#C2831A" : "#B91C1C" }} />
                </div>
                <p className="text-xs text-[#6B7280]">{g.comentario}</p>
              </div>
            ))}
          </div>
        </Sec>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <Sec title="Valores comuns" tone="emerald">
          <ul className="space-y-1.5 text-sm">{(a.valoresComuns || []).map((v, i) => <li key={i} className="flex gap-2"><CheckCircle2 size={15} className="text-emerald-600 shrink-0 mt-0.5" />{v}</li>)}</ul>
        </Sec>
        <Sec title="Compatíveis (não comuns)" tone="amber">
          <ul className="space-y-2 text-sm">{(a.valoresCompativeis || []).map((v, i) => (
            <li key={i}><span className="font-medium">{v.valor1}</span> + <span className="font-medium">{v.valor2}</span>
              <p className="text-xs text-[#6B7280]">{v.comentario}</p></li>))}</ul>
        </Sec>
        <Sec title="Valores incompatíveis" tone="rose">
          {(a.valoresIncompativeis || []).length === 0 && <p className="text-sm text-[#978D78]">Nenhum identificado 🎉</p>}
          <ul className="space-y-2 text-sm">{(a.valoresIncompativeis || []).map((v, i) => (
            <li key={i}><span className="font-medium">{v.valor1}</span> vs <span className="font-medium">{v.valor2}</span>
              <p className="text-xs text-rose-600">{v.risco}</p></li>))}</ul>
        </Sec>
      </div>

      {(a.metaprogramas || []).length > 0 && (
        <Sec title="Metaprogramas — riscos potenciais de conflito">
          <div className="space-y-3">
            {a.metaprogramas.map((m, i) => (
              <div key={i} className="rounded-lg border border-[#F3F0E7] p-3" data-testid={`casal-metaprograma-${i}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[#0F1829]">{m.metaprograma}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${RISCO_CLS[(m.riscoConflito || "").toLowerCase()] || "bg-slate-100 text-slate-600"}`}>
                    risco {m.riscoConflito}
                  </span>
                </div>
                <p className="text-xs text-[#6B7280] mt-1">Tu: {m.perfil1} · {p2}: {m.perfil2}</p>
                <p className="text-sm text-[#4B5563] mt-1">{m.comentario}</p>
              </div>
            ))}
          </div>
        </Sec>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Sec title="Pontos fortes do casal" tone="emerald">
          <ul className="space-y-1.5 text-sm">{(a.pontosFortes || []).map((v, i) => <li key={i} className="flex gap-2"><CheckCircle2 size={15} className="text-emerald-600 shrink-0 mt-0.5" />{v}</li>)}</ul>
        </Sec>
        <Sec title="Recomendações (próximas 2 semanas)">
          <ul className="space-y-2.5 text-sm">{(a.recomendacoes || []).map((r, i) => (
            <li key={i}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="font-medium text-[#0F1829]">{r.titulo}</span>
                  <p className="text-xs text-[#6B7280]">{r.acao}</p>
                </div>
                {jaGuardada(r.titulo) ? (
                  <span className="text-[11px] text-emerald-600 whitespace-nowrap inline-flex items-center gap-1"><CheckCircle2 size={12} /> meta</span>
                ) : (
                  <button onClick={() => onGuardarMeta?.(r)}
                    className="text-[11px] text-[#4A5E2E] hover:underline whitespace-nowrap inline-flex items-center gap-1"
                    title="Guardar como meta do casal"
                    data-testid={`casal-guardar-meta-${i}`}>
                    <BookmarkPlus size={12} /> Guardar meta
                  </button>
                )}
              </div>
            </li>))}</ul>
        </Sec>
      </div>

      {a.sintese && (
        <div className="rounded-xl bg-[#0F1829] text-white p-4 text-sm leading-relaxed" data-testid="casal-sintese">
          <p className="flex items-start gap-2"><AlertTriangle size={16} className="shrink-0 mt-0.5 text-[#C2831A]" />{a.sintese}</p>
        </div>
      )}
    </div>
  );
}

function MetasCard({ metas, reload }) {
  if (!metas.length) return null;
  const feitas = metas.filter((m) => m.concluida).length;

  const toggle = async (m) => {
    try { await myApi.casalMetaToggle(m.id); await reload(); }
    catch (e) { alert(e.message); }
  };
  const apagar = async (m) => {
    if (!window.confirm(`Apagar a meta "${m.titulo}"?`)) return;
    try { await myApi.casalMetaApagar(m.id); await reload(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm" data-testid="casal-metas-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="text-base font-semibold text-[#0F1829] flex items-center gap-2"><Target size={16} /> Metas do casal</h3>
        <span className="text-xs text-[#978D78]">{feitas} de {metas.length} concluídas</span>
      </div>
      <p className="text-xs text-[#978D78] mb-3">Recebem por email um ponto de situação mensal das metas em curso.</p>
      <div className="h-1.5 bg-[#F3F0E7] rounded-full mb-4">
        <div className="h-1.5 rounded-full bg-[#4A5E2E]" style={{ width: `${metas.length ? (feitas / metas.length) * 100 : 0}%` }} />
      </div>
      <ul className="space-y-2.5">
        {metas.map((m) => (
          <li key={m.id} className="flex items-start gap-3" data-testid={`casal-meta-${m.id}`}>
            <button onClick={() => toggle(m)} aria-label="Alternar meta"
              className={`mt-0.5 w-5 h-5 rounded-full border grid place-items-center shrink-0 ${m.concluida ? "bg-[#4A5E2E] border-[#4A5E2E] text-white" : "border-[#C9C2B0] text-transparent hover:border-[#4A5E2E]"}`}
              data-testid={`casal-meta-toggle-${m.id}`}>
              <CheckCircle2 size={13} />
            </button>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${m.concluida ? "line-through text-[#978D78]" : "text-[#0F1829]"}`}>{m.titulo}</p>
              {m.acao && <p className="text-xs text-[#6B7280]">{m.acao}</p>}
            </div>
            <button onClick={() => apagar(m)} className="text-[#C9C2B0] hover:text-rose-600" aria-label="Apagar meta"
              data-testid={`casal-meta-apagar-${m.id}`}><Trash2 size={15} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Sec({ title, tone, children }) {
  const border = tone === "emerald" ? "border-emerald-100" : tone === "amber" ? "border-amber-100" : tone === "rose" ? "border-rose-100" : "border-[#F3F0E7]";
  return (
    <div className={`rounded-xl border ${border} p-4 bg-[#FBFAF7]`}>
      <h4 className="text-sm font-semibold text-[#0F1829] mb-2">{title}</h4>
      {children}
    </div>
  );
}

function ShareCasalModal({ onClose, p1 }) {
  const [busy, setBusy] = useState(true);
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [revoked, setRevoked] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await myApi.casalPartilhar();
        if (alive) setInfo(r);
      } catch (e) { if (alive) setErr(e.message); }
      finally { if (alive) setBusy(false); }
    })();
    return () => { alive = false; };
  }, []);

  const shareUrl = info ? `${window.location.origin}/api/coach/casal/s/${info.token}` : "";
  const cardUrl = info ? `${window.location.origin}/api/coach/casal/share/${info.token}/card.png` : "";
  const shareText = info
    ? `A nossa compatibilidade de casal é ${info.compat}%! Descobre a tua no IHTP Coach Virtual 💛`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  const nativeShare = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({ title: "A nossa compatibilidade de casal", text: shareText, url: shareUrl });
    } catch { /* utilizador cancelou */ }
  };

  const revogar = async () => {
    if (!window.confirm("Revogar o link — quem já tem o URL deixa de conseguir abrir. Confirmas?")) return;
    try { await myApi.casalPartilharRevogar(); setRevoked(true); }
    catch (e) { alert(e.message); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose} data-testid="casal-share-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <h3 className="text-base font-semibold flex items-center gap-2 text-[#0F1829]">
            <Share2 size={16} className="text-[#4A5E2E]" /> Partilhar compatibilidade
          </h3>
          <button onClick={onClose} className="text-[#978D78] hover:text-[#0F1829]"
            data-testid="casal-share-close" aria-label="Fechar"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {busy && (
            <div className="flex items-center gap-2 text-sm text-[#978D78]">
              <Loader2 size={16} className="animate-spin" /> A preparar o cartão…
            </div>
          )}
          {err && (
            <p className="text-sm text-rose-600" data-testid="casal-share-err">
              {err}. {p1 && `Precisas de ter a análise com o(a) ${p1} gerada primeiro.`}
            </p>
          )}
          {revoked && (
            <p className="text-sm rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-700">
              Link revogado. Fecha esta janela e volta a abrir para gerar outro.
            </p>
          )}

          {info && !revoked && (
            <>
              {/* Preview do cartão gerado */}
              <div className="rounded-xl border border-[#E5E7EB] overflow-hidden bg-[#F5F2EB]"
                data-testid="casal-share-preview">
                <img src={cardUrl} alt="Cartão de compatibilidade"
                  className="w-full h-auto block" />
              </div>

              {/* URL + copy */}
              <div>
                <label className="text-xs uppercase tracking-widest text-[#978D78]">Link</label>
                <div className="flex gap-2 mt-1">
                  <input value={shareUrl} readOnly
                    className="flex-1 px-3 py-2 rounded-lg border border-[#E5E7EB] text-sm font-mono bg-[#FAF8F3]"
                    data-testid="casal-share-url" onClick={(e) => e.target.select()} />
                  <button onClick={copy}
                    className="px-3 py-2 rounded-lg bg-[#0F1829] text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:opacity-90"
                    data-testid="casal-share-copy">
                    <Copy size={14} /> {copied ? "Copiado!" : "Copiar"}
                  </button>
                </div>
              </div>

              {/* Botões de partilha */}
              <div className="grid grid-cols-2 gap-2">
                <a href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#25D366] text-white text-sm font-semibold hover:opacity-90"
                  data-testid="casal-share-whatsapp">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.043 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.511-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.456 3.488"/></svg>
                  WhatsApp
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1877F2] text-white text-sm font-semibold hover:opacity-90"
                  data-testid="casal-share-facebook">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Facebook
                </a>
                <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0F1829] text-white text-sm font-semibold hover:opacity-90"
                  data-testid="casal-share-twitter">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  X (Twitter)
                </a>
                <a href={cardUrl} download={`compatibilidade-casal-ihtp.png`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#E5E7EB] text-[#0F1829] text-sm font-semibold hover:bg-[#F3F4F6]"
                  data-testid="casal-share-download">
                  <Download size={14} /> PNG
                </a>
              </div>

              {typeof navigator !== "undefined" && navigator.share && (
                <button onClick={nativeShare}
                  className="w-full px-4 py-2.5 rounded-lg bg-[#4A5E2E] text-white text-sm font-semibold hover:opacity-90"
                  data-testid="casal-share-native">
                  Partilhar por…
                </button>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-[#F3F0E7]">
                <p className="text-xs text-[#978D78]">
                  Ao partilhar, o cartão fica visível a quem tiver o link.
                </p>
                <button onClick={revogar}
                  className="text-xs text-rose-600 hover:underline"
                  data-testid="casal-share-revogar">Revogar link</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
